'use client';

import AnimatedArrow from '@/components/common/AnimatedArrow';
import Container from '@/components/ui/Container';
import HeroSectionHeading from '@/components/ui/HeroSectionHeading';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import React, { useState, useMemo } from 'react';
import { ourWorkPage } from '../utils/page-data/ourWork';

type SubFilterMap = {
  Industries: string;
  Services: string;
};

const OurWorks = () => {
  const [selectedTab, setSelectedTab] = useState<string>('All');
  const [subFilterMap, setSubFilterMap] = useState<SubFilterMap>({
    Industries: ourWorkPage.industriesList[0] ?? '',
    Services: ourWorkPage.servicesList[0] ?? '',
  });
  const [hover, setHover] = useState(false);

  const handleMouseEnter = () => setHover(true);
  const handleMouseLeave = () => setHover(false);

  const subOptions: string[] =
    selectedTab === 'Industries'
      ? ourWorkPage.industriesList
      : selectedTab === 'Services'
        ? ourWorkPage.servicesList
        : [];

  const selectedSubFilter =
    selectedTab === 'Industries'
      ? subFilterMap.Industries
      : selectedTab === 'Services'
        ? subFilterMap.Services
        : '';

  const filteredProjects = useMemo(() => {
    if (selectedTab === 'All') return ourWorkPage.projects;
    return ourWorkPage.projects.filter((project) => {
      if (selectedTab === 'Industries')
        return project.industries?.includes(selectedSubFilter);
      if (selectedTab === 'Services')
        return project.services?.includes(selectedSubFilter);
      return false;
    });
  }, [selectedTab, selectedSubFilter]);

  const handleTabChange = (tab: string) => setSelectedTab(tab);

  const handleSubFilterChange = (option: string) => {
    if (selectedTab === 'Industries') {
      setSubFilterMap((prev) => ({ ...prev, Industries: option }));
    } else if (selectedTab === 'Services') {
      setSubFilterMap((prev) => ({ ...prev, Services: option }));
    }
  };

  return (
    <>
      {/* Hero Section */}
      <div
        className="-mt-20 bg-aboutUsHeroBg bg-cover bg-no-repeat"
        style={{ backgroundSize: '100% 100%' }}
      >
        <Container className="mx-auto flex w-full max-w-[1000px] flex-col items-center justify-between gap-12 py-[100px] lg:flex-row">
          <HeroSectionHeading
            heading={ourWorkPage?.headingTitle}
            description={ourWorkPage?.headingDescription}
            className="w-full"
          />
          <div className="relative aspect-[550/425] h-full w-full max-w-[550px]">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: 'easeIn' }}
              className="relative h-full w-full"
            >
              <Image
                src={ourWorkPage?.heroRightImage || ''}
                alt={ourWorkPage?.heroRightImage || 'our-work-hero'}
                fill
                priority
                fetchPriority="high"
                className="object-contain"
              />
            </motion.div>
          </div>
        </Container>
      </div>

      <Container className="-mt-[30px] mb-[60px] flex flex-col gap-[60px] 3xl:mb-[100px]">
        <div className="flex flex-col gap-5">
          {/* Top Tabs */}
          <div className="flex items-center justify-center">
            <div className="flex gap-5 overflow-auto whitespace-pre rounded-full border border-black-100-alpha bg-snowWhite p-2 scrollbar-hide">
              {ourWorkPage.tabs.map((tab, index) => (
                <div
                  key={index}
                  className="relative w-full cursor-pointer rounded-full"
                >
                  {selectedTab === tab && (
                    <motion.div
                      layoutId="active-category-tab"
                      className="play-button pointer-events-none absolute inset-0 h-full w-full rounded-full border-[1.591px] border-dustyBlue"
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30,
                      }}
                    />
                  )}
                  <div
                    onClick={() => handleTabChange(tab)}
                    className={`flex h-full items-center justify-center rounded-full px-6 py-2 font-opt text-base font-normal text-optDesc ${
                      selectedTab === tab
                        ? 'bg-whiteSmoke'
                        : 'border border-iceGray bg-white'
                    }`}
                  >
                    {tab}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sub-filter row — always mounted when tab != All, no AnimatePresence */}
          {selectedTab !== 'All' && (
            <motion.div
              key={selectedTab}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="flex items-center justify-center"
            >
              <div className="flex gap-5 overflow-auto whitespace-pre rounded-full border border-black-100-alpha bg-snowWhite p-2 scrollbar-hide">
                {subOptions.map((option) => (
                  <div
                    key={option}
                    className="relative w-full cursor-pointer rounded-full"
                  >
                    {selectedSubFilter === option && (
                      <motion.div
                        layoutId={`active-sub-tab-${selectedTab}`}
                        className="play-button pointer-events-none absolute inset-0 h-full w-full rounded-full border-[1.591px] border-dustyBlue"
                      />
                    )}
                    <div
                      onClick={() => handleSubFilterChange(option)}
                      className={`flex h-full items-center justify-center rounded-full px-6 py-2.5 font-opt text-base font-normal leading-5 text-optDesc ${
                        selectedSubFilter === option
                          ? 'bg-whiteSmoke'
                          : 'border border-iceGray bg-white'
                      }`}
                    >
                      {option}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Projects Grid + CTA Sidebar */}
        <div className="grid grid-cols-1 gap-6 gap-x-0 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
          <div className="col-span-2">
            {filteredProjects.length ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {filteredProjects.map((item, idx) => (
                  <Link
                    prefetch={true}
                    href={`/our-work/${item.redirectlink}`}
                    scroll={true}
                    key={idx}
                    className="group transition-transform duration-300 ease-in-out hover:scale-[1.03]"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, ease: 'easeIn' }}
                    >
                      <div className="rounded-xl border border-black/10 bg-primarySnowGray p-2">
                        <div className="flex flex-col gap-4 rounded-md bg-white p-2">
                          <div className="relative">
                            <Image
                              src={item?.image}
                              alt={item?.title}
                              width={322}
                              height={182}
                              className="w-full rounded-md object-cover"
                            />
                          </div>
                          <div className="flex flex-col justify-between gap-1.5 px-2">
                            <p className="font-opt text-lg font-semibold leading-6 text-primary group-hover:text-lightBlue">
                              {item?.title}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-2xl text-optDesc">Not found.....</div>
            )}
          </div>

          {/* CTA Sidebar */}
          <div className="top-24 flex h-max w-full flex-col gap-16 rounded-xl bg-contactUsBg bg-cover bg-center bg-no-repeat p-8 will-change-transform lg:sticky 3xl:bg-full">
            <div className="flex flex-col gap-5">
              <p className="font-opt text-xl font-medium leading-6 text-white">
                Ready to transform your business?
              </p>
              <p className="font-opt text-base font-light leading-5 text-white">
                Let&#39;s connect and discuss how we can help you build
                scalable, high-performance digital solutions. Whether you need a
                modern web application, backend system, or complete product
                development, our team is here to bring your ideas to life.
              </p>
            </div>
            <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseEnter}
              onMouseDown={handleMouseLeave}
              className="w-max"
            >
              <Link
                href="/contact-us"
                className="flex w-max items-center gap-1 rounded-[50px] border border-transparent bg-white px-[16px] py-[8px] pr-[14px] font-opt text-sm font-normal leading-5 text-lightBlue transition-all duration-200 ease-in hover:border-white hover:bg-transparent hover:text-white"
              >
                Contact us
                <AnimatedArrow hover={hover} />
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default OurWorks;
